import { type AsyncResult, SynchrounousState } from "@/cache/AsyncResult";
import type { WmtsTile } from "@/cache/PVSTileService/TileGetters/WMTSTileGetter/WMTSTile";
import { instantiateTiles } from "@/cache/SpatioTemporalTileCache/TileArea";
import { TileGeometry } from "@/cache/SpatioTemporalTileCache/TileGeometry";
import type { ScenePublicApi } from "@/layers/Compositor";
import type { MapViewport } from "@/layers/MapViewport";
import { isPlaceholderTile } from "@/utility/typeGuard";
import type { GridDimension } from "@mm/api.meteomatics.com";
import type { InterpolationMode } from "@mm/weather-parameter-utils";
import {
  type MainpassProgram,
  type PrepassFramebuffer,
  type PrepassProgram,
  createMainpassProgram,
  createPrepassFramebuffer,
  createPrepassProgram,
} from "../shaderPrograms/programs";
import type { WmtsRenderableTileSets } from "./WMTSTileManager";
import { calculateTextureCoordinates } from "./utils";

type TileOpacity = { tile: WmtsTile; opacity: number };
type RenderedTile = { tile: WmtsTile; source: "backing" | "ideal" };

/**
 * Handles all WebGL rendering for the WMTS layer.
 * Technique: Standard-Matrix (u_mercatorToGl from Mapbox) + Texture-Sampling for raster tiles.
 */
export class WMTSRenderer {
  private readonly IDEAL_TILE_FADE_MS = 250;
  private readonly TIME_STEP_FADE_MS = 250;
  private prepassProgram?: AsyncResult<PrepassProgram>;
  private _prepassFramebuffer?: PrepassFramebuffer;
  private _previousPrepassFramebuffer?: PrepassFramebuffer;
  private mainpassProgram?: AsyncResult<MainpassProgram>;
  private readonly MAX_DATA_ZOOM = 11; // WMTS cap
  private idealTileFadeStartMs = new Map<string, number>();
  private settledIdealTileKeys = new Set<string>();
  private previousRenderedTiles: RenderedTile[] = [];
  private lastRenderedTimeKey: string | null = null;
  private lastRenderedViewKey: string | null = null;
  private pendingTimeStepFade = false;
  private timeStepFadeStartMs: number | null = null;
  private lastAnimationMode = false;

  constructor(
    gl: WebGL2RenderingContext,
    private scene: ScenePublicApi,
    private interpolationMode: InterpolationMode,
  ) {
    this.prepassProgram = createPrepassProgram(gl);
    this.mainpassProgram = createMainpassProgram(gl);

    // Program compilation is async. If both shaders finish after the last tile-ready repaint,
    // WMTS can stay blank until some unrelated interaction triggers a new frame.
    // Force one repaint on readiness so the first visible draw happens immediately.
    this.prepassProgram.asynchronous.then(() => this.scene.repaint()).catch(() => undefined);
    this.mainpassProgram.asynchronous.then(() => this.scene.repaint()).catch(() => undefined);
  }

  /**
   * The main pre-rendering pass. Draws the given tiles to an offscreen framebuffer.
   */
  public prerender(gl: WebGL2RenderingContext, matrix: number[], tileSets: WmtsRenderableTileSets): void {
    const size = this.scene.getRenderSize();
    const viewport = this.scene.getMapViewport();
    const isAnimationMode = this.scene.isAnimation();
    const isActivelyAnimating = tileSets.isActivelyAnimating ?? false;
    const currentZoom = viewport.zoom;
    const currentTileZoom = Math.floor(currentZoom);
    const {
      backingTiles,
      idealTiles,
      idealTilesReady,
      suppressRetainedBacking = false,
      disableTransitions = false,
    } = tileSets;
    const nextTimeKey = this.getTileTimeKey(tileSets);
    const nextViewKey = this.getViewKey(viewport, size);

    this.prepareTemporalFade(gl, size, nextTimeKey, nextViewKey, disableTransitions);
    this.syncAnimationMode(gl, isAnimationMode);

    const fb = this.getPrepassFramebuffer(gl, size, this.interpolationMode);

    if (!this.prepassProgram) {
      return;
    }

    const prepass = this.prepassProgram.synchronous;

    if (
      prepass === SynchrounousState.StillPending ||
      prepass === SynchrounousState.PermanentlyFailed ||
      prepass === SynchrounousState.NotRequested
    ) {
      return;
    }

    this.setupFramebuffer(gl, prepass, fb, size, matrix);

    if (backingTiles.length === 0 && idealTiles.length === 0) {
      gl.disable(gl.BLEND);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      this.lastRenderedTimeKey = nextTimeKey;
      this.lastRenderedViewKey = nextViewKey;
      return;
    }

    const now = Date.now();
    const loadedBackingTiles = backingTiles.filter((tile) => tile.isLoaded && !isPlaceholderTile(tile));
    const loadedIdealTiles = idealTiles.filter((tile) => tile.isLoaded && !isPlaceholderTile(tile));
    if (disableTransitions) {
      this.idealTileFadeStartMs.clear();
      for (const tile of loadedIdealTiles) {
        this.settledIdealTileKeys.add(tile.toKey());
      }
    }
    const loadedCurrentTiles = [...loadedBackingTiles, ...loadedIdealTiles];
    const loadedCurrentKeys = new Set(loadedCurrentTiles.map((tile) => tile.toKey()));
    const retainedTiles =
      this.pendingTimeStepFade || isActivelyAnimating || disableTransitions
        ? []
        : this.previousRenderedTiles.filter(
            ({ tile, source }) =>
              !loadedCurrentKeys.has(tile.toKey()) && !(suppressRetainedBacking && source === "backing"),
          );
    this.pruneFadeState(idealTiles);

    const retainedTileEntries = retainedTiles.map(({ tile }) => tile);
    const shouldRenderBackingTiles = !this.pendingTimeStepFade && !isActivelyAnimating && !disableTransitions;
    const fallbackCoverageTiles = [...retainedTileEntries, ...(shouldRenderBackingTiles ? loadedBackingTiles : [])];
    const retainedBackingTiles = retainedTiles.filter(({ source }) => source === "backing");
    const retainedIdealTiles = retainedTiles.filter(({ source }) => source === "ideal");
    const loadedIdealTilesWithOpacity = loadedIdealTiles.map((tile) => ({
      tile,
      opacity:
        disableTransitions || this.pendingTimeStepFade ? 1 : this.getIdealTileOpacity(tile, fallbackCoverageTiles, now),
    }));
    const settledIdealTilesWithOpacity = loadedIdealTilesWithOpacity
      .filter(({ opacity }) => opacity >= 1)
      .map(({ tile }) => ({ tile, opacity: 1 }));

    let hasActiveFade = false;
    const renderedTilesThisFrame = new Map<string, RenderedTile>();

    for (const retainedTile of [...retainedBackingTiles].sort((a, b) => a.tile.z - b.tile.z)) {
      const { tile, source } = retainedTile;
      const texture = tile.texture(gl);
      gl.bindTexture(gl.TEXTURE_2D, texture);

      const depth = tile.z / (this.MAX_DATA_ZOOM + 5);
      if (this.renderTile(gl, prepass, tile, currentZoom, currentTileZoom, viewport, depth, 1)) {
        renderedTilesThisFrame.set(tile.toKey(), { tile, source });
      }
    }

    for (const retainedTile of [...retainedIdealTiles].sort((a, b) => a.tile.z - b.tile.z)) {
      const { tile, source } = retainedTile;
      const texture = tile.texture(gl);
      gl.bindTexture(gl.TEXTURE_2D, texture);

      const depth = tile.z / (this.MAX_DATA_ZOOM + 5);
      const replacementCoverage = source === "ideal" ? settledIdealTilesWithOpacity : loadedIdealTilesWithOpacity;
      if (this.renderTile(gl, prepass, tile, currentZoom, currentTileZoom, viewport, depth, 1, replacementCoverage)) {
        renderedTilesThisFrame.set(tile.toKey(), { tile, source });
      }
    }

    if (shouldRenderBackingTiles && backingTiles.length > 0) {
      this.setTransparentFillBlendMode(gl);
      for (const tile of [...backingTiles].sort((a, b) => b.z - a.z)) {
        if (!tile.isLoaded) {
          continue;
        }
        if (isPlaceholderTile(tile)) {
          continue;
        }

        const texture = tile.texture(gl);
        gl.bindTexture(gl.TEXTURE_2D, texture);

        const depth = tile.z / (this.MAX_DATA_ZOOM + 5);
        if (this.renderTile(gl, prepass, tile, currentZoom, currentTileZoom, viewport, depth, 1)) {
          renderedTilesThisFrame.set(tile.toKey(), { tile, source: "backing" });
        }
      }
      this.setStandardBlendMode(gl);
    }

    for (const { tile, opacity } of [...loadedIdealTilesWithOpacity].sort((a, b) => a.tile.z - b.tile.z)) {
      if (opacity <= 0) {
        hasActiveFade = true;
        continue;
      }
      if (opacity < 1) {
        hasActiveFade = true;
      }

      const texture = tile.texture(gl);
      gl.bindTexture(gl.TEXTURE_2D, texture);

      const depth = tile.z / (this.MAX_DATA_ZOOM + 5);
      if (this.renderTile(gl, prepass, tile, currentZoom, currentTileZoom, viewport, depth, opacity)) {
        renderedTilesThisFrame.set(tile.toKey(), { tile, source: "ideal" });
      }
    }

    if (hasActiveFade && !disableTransitions) {
      this.scene.repaint();
    }

    if (
      this.pendingTimeStepFade &&
      this.timeStepFadeStartMs == null &&
      idealTilesReady &&
      renderedTilesThisFrame.size > 0
    ) {
      this.timeStepFadeStartMs = now;
      this.scene.repaint();
    }

    this.previousRenderedTiles = [...renderedTilesThisFrame.values()];
    this.lastRenderedTimeKey = nextTimeKey;
    this.lastRenderedViewKey = nextViewKey;
    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /**
   * The main pass. Renders the framebuffer texture to the screen with opacity.
   */
  public render(gl: WebGL2RenderingContext, opacity: number): void {
    const fb = this._prepassFramebuffer;
    if (!fb || !this.mainpassProgram) {
      return;
    }

    const mainpass = this.mainpassProgram.synchronous;
    if (
      mainpass === SynchrounousState.StillPending ||
      mainpass === SynchrounousState.PermanentlyFailed ||
      mainpass === SynchrounousState.NotRequested
    ) {
      return;
    }

    const size = this.scene.getRenderSize();

    gl.useProgram(mainpass.program);
    gl.disable(gl.DEPTH_TEST);
    gl.viewport(0, 0, size.width, size.height);
    gl.bindBuffer(gl.ARRAY_BUFFER, mainpass.buffer_TextureCoordinates);
    gl.enableVertexAttribArray(mainpass.a_position);
    gl.vertexAttribPointer(mainpass.a_position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(mainpass.u_sampler, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.uniform1i(mainpass.u_samplerPrevious, 1);
    gl.uniform4f(mainpass.u_bcs, 1.0, 1.0, 1.0, 0.0);

    const previousFb = this._previousPrepassFramebuffer;
    const hasStartedTimeStepFade = this.timeStepFadeStartMs != null;
    const timeStepFadeOpacity = this.getTimeStepFadeOpacity();
    const hasActiveTimeStepFade = previousFb != null && this.pendingTimeStepFade;

    const previousTexture = hasActiveTimeStepFade ? previousFb.color : fb.color;
    const temporalInterpolation = hasActiveTimeStepFade ? timeStepFadeOpacity : 1;

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    this.renderMainpassTextures(gl, mainpass, previousTexture, fb.color, opacity, temporalInterpolation, size);

    if (hasActiveTimeStepFade) {
      if (hasStartedTimeStepFade && timeStepFadeOpacity < 1) {
        this.scene.repaint();
      } else if (hasStartedTimeStepFade) {
        this.finishTimeStepFade(gl);
      }
    }
  }

  private renderNormalTile(
    gl: WebGL2RenderingContext,
    prepass: any,
    tile: WmtsTile,
    viewport: any,
    depth: number,
    opacity: number,
  ): boolean {
    const tileGeom = new TileGeometry([tile.x, tile.y, tile.z]);
    const instances = [...instantiateTiles(this.getInstantiatedViewportArea(viewport), tileGeom)];
    const fullTexture = { texLeft: 0, texRight: 1, texTop: 0, texBottom: 1 };
    let rendered = false;

    for (const instance of instances) {
      const [n, w, s, e] = instance.mercator(0);
      this.renderTileInstance(gl, prepass, w, s, e, n, fullTexture, depth, opacity);
      rendered = true;
    }

    return rendered;
  }

  private renderScaledTile(
    gl: WebGL2RenderingContext,
    prepass: any,
    tile: WmtsTile,
    currentMapZoom: number,
    viewport: MapViewport,
    depth: number,
    opacity: number,
    replacementIdealTiles?: readonly TileOpacity[],
  ): boolean {
    const currentTileZoom = Math.floor(currentMapZoom);
    const zoomDiff = currentTileZoom - tile.z;
    const scaleFactor = 2 ** zoomDiff;
    const visibleRange = this.getVisibleScaledTileRange(tile, currentTileZoom, viewport);
    if (!visibleRange) {
      return false;
    }
    const unbiasedViewportArea = this.getInstantiatedViewportArea(viewport);
    let rendered = false;

    for (let dy = visibleRange.minDy; dy <= visibleRange.maxDy; dy++) {
      for (let dx = visibleRange.minDx; dx <= visibleRange.maxDx; dx++) {
        const virtualX = tile.x * scaleFactor + dx;
        const virtualY = tile.y * scaleFactor + dy;
        const effectiveOpacity =
          opacity *
          (1 - this.getCurrentTileReplacementOpacity(virtualX, virtualY, currentTileZoom, replacementIdealTiles));
        if (effectiveOpacity <= 0) {
          continue;
        }

        const virtualTileGeometry = new TileGeometry([virtualX, virtualY, currentTileZoom]);
        const instances = [...instantiateTiles(unbiasedViewportArea, virtualTileGeometry)];
        const texCoords = calculateTextureCoordinates(dx, dy, scaleFactor);

        for (const instance of instances) {
          const [n, w, s, e] = instance.mercator(0);
          this.renderTileInstance(gl, prepass, w, s, e, n, texCoords, depth, effectiveOpacity);
          rendered = true;
        }
      }
    }

    return rendered;
  }

  private renderClippedTile(
    gl: WebGL2RenderingContext,
    prepass: any,
    tile: WmtsTile,
    currentMapZoom: number,
    viewport: any,
    depth: number,
    opacity: number,
  ): boolean {
    const currentTileZoom = Math.floor(currentMapZoom);
    const zoomDiff = tile.z - currentTileZoom;
    if (zoomDiff <= 0) {
      return false;
    }

    const unbiasedViewportArea = this.getInstantiatedViewportArea(viewport);

    const scaleFactor = 2 ** zoomDiff;
    const ancestorX = Math.floor(tile.x / scaleFactor);
    const ancestorY = Math.floor(tile.y / scaleFactor);
    const ancestorGeom = new TileGeometry([ancestorX, ancestorY, currentTileZoom]);

    const ancestorInstances = [...instantiateTiles(unbiasedViewportArea, ancestorGeom)];
    if (ancestorInstances.length === 0) {
      return false;
    }

    const childIndexX = tile.x % scaleFactor;
    const childIndexY = tile.y % scaleFactor;
    const fullTexture = { texLeft: 0, texRight: 1, texTop: 0, texBottom: 1 };

    for (const instance of ancestorInstances) {
      const [ancestorN, ancestorW, ancestorS, ancestorE] = instance.mercator(0);

      const ancestorWidth = ancestorE - ancestorW;
      const ancestorHeight = ancestorN - ancestorS;
      const childWidth = ancestorWidth / scaleFactor;
      const childHeight = ancestorHeight / scaleFactor;
      const childW = ancestorW + childIndexX * childWidth;
      const childE = childW + childWidth;
      const childN = ancestorN - childIndexY * childHeight;
      const childS = childN - childHeight;

      this.renderTileInstance(gl, prepass, childW, childS, childE, childN, fullTexture, depth, opacity);
    }

    return true;
  }

  private renderTile(
    gl: WebGL2RenderingContext,
    prepass: any,
    tile: WmtsTile,
    currentMapZoom: number,
    currentTileZoom: number,
    viewport: MapViewport,
    depth: number,
    opacity: number,
    replacementIdealTiles?: readonly TileOpacity[],
  ): boolean {
    const needsScalingUp = tile.z < currentTileZoom;
    const needsClippingDown = tile.z > currentTileZoom;

    if (needsScalingUp) {
      return this.renderScaledTile(gl, prepass, tile, currentMapZoom, viewport, depth, opacity, replacementIdealTiles);
    }

    if (needsClippingDown) {
      const scaleFactor = 2 ** (tile.z - currentTileZoom);
      const ancestorX = Math.floor(tile.x / scaleFactor);
      const ancestorY = Math.floor(tile.y / scaleFactor);
      const effectiveOpacity =
        opacity *
        (1 - this.getCurrentTileReplacementOpacity(ancestorX, ancestorY, currentTileZoom, replacementIdealTiles));
      if (effectiveOpacity <= 0) {
        return false;
      }
      return this.renderClippedTile(gl, prepass, tile, currentMapZoom, viewport, depth, effectiveOpacity);
    }

    const effectiveOpacity =
      opacity * (1 - this.getCurrentTileReplacementOpacity(tile.x, tile.y, currentTileZoom, replacementIdealTiles));
    if (effectiveOpacity <= 0) {
      return false;
    }
    return this.renderNormalTile(gl, prepass, tile, viewport, depth, effectiveOpacity);
  }

  private renderTileInstance(
    gl: WebGL2RenderingContext,
    prepass: any,
    w: number,
    s: number,
    e: number,
    n: number,
    texCoords: { texLeft: number; texRight: number; texTop: number; texBottom: number },
    depth: number,
    opacity: number,
  ): void {
    gl.uniform1f(prepass.u_opacity, opacity);
    this.updateTextureCoordinateBuffer(gl, prepass, texCoords);
    this.updateVertexBuffer(gl, prepass, w, s, e, n, depth);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private updateTextureCoordinateBuffer(
    gl: WebGL2RenderingContext,
    prepass: any,
    texCoords: { texLeft: number; texRight: number; texTop: number; texBottom: number },
  ): void {
    gl.bindBuffer(gl.ARRAY_BUFFER, prepass.buffer_TextureCoordinates);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        texCoords.texLeft,
        texCoords.texBottom,
        texCoords.texRight,
        texCoords.texBottom,
        texCoords.texLeft,
        texCoords.texTop,
        texCoords.texLeft,
        texCoords.texTop,
        texCoords.texRight,
        texCoords.texBottom,
        texCoords.texRight,
        texCoords.texTop,
      ]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(prepass.a_texCoord);
    gl.vertexAttribPointer(prepass.a_texCoord, 2, gl.FLOAT, false, 0, 0);
  }

  private updateVertexBuffer(
    gl: WebGL2RenderingContext,
    prepass: any,
    w: number,
    s: number,
    e: number,
    n: number,
    depth: number,
  ): void {
    gl.bindBuffer(gl.ARRAY_BUFFER, prepass.buffer_QuadVertices);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([w, s, depth, e, s, depth, w, n, depth, w, n, depth, e, s, depth, e, n, depth]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(prepass.a_position);
    gl.vertexAttribPointer(prepass.a_position, 3, gl.FLOAT, false, 0, 0);
  }

  private setupFramebuffer(
    gl: WebGL2RenderingContext,
    prepass: PrepassProgram,
    fb: PrepassFramebuffer,
    size: GridDimension,
    matrix: number[],
  ): void {
    gl.useProgram(prepass.program);
    gl.viewport(0, 0, size.width, size.height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, fb.color, 0);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, null);

    gl.clearColor(0, 0, 0, 0);
    gl.clearDepth(0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    this.setStandardBlendMode(gl);

    gl.uniformMatrix4fv(prepass.u_mercatorToGl, false, matrix);
    gl.uniform1f(prepass.u_opacity, 1.0);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(prepass.u_tileSampler, 0);
  }

  private pruneFadeState(idealTiles: WmtsTile[]): void {
    const activeKeys = new Set(idealTiles.map((tile) => tile.toKey()));

    for (const key of this.idealTileFadeStartMs.keys()) {
      if (!activeKeys.has(key)) {
        this.idealTileFadeStartMs.delete(key);
      }
    }
  }

  private setStandardBlendMode(gl: WebGL2RenderingContext): void {
    // Tiles are written with premultiplied alpha (`fragColor = data * opacity`),
    // so the fade should only bring the new tile in over the existing backing tile.
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  private setTransparentFillBlendMode(gl: WebGL2RenderingContext): void {
    // Fill only the still-transparent pixels in the prepass. This allows backing
    // tiles to cover holes without showing underneath already rendered concrete tiles.
    gl.blendFunc(gl.ONE_MINUS_DST_ALPHA, gl.ONE);
  }

  private getIdealTileOpacity(tile: WmtsTile, loadedBackingTiles: WmtsTile[], now: number): number {
    const tileKey = tile.toKey();

    if (this.settledIdealTileKeys.has(tileKey)) {
      return 1;
    }

    if (!this.hasLoadedBackingCoverage(tile, loadedBackingTiles)) {
      this.idealTileFadeStartMs.delete(tileKey);
      this.settledIdealTileKeys.add(tileKey);
      return 1;
    }

    const fadeStart = this.idealTileFadeStartMs.get(tileKey) ?? now;
    if (!this.idealTileFadeStartMs.has(tileKey)) {
      this.idealTileFadeStartMs.set(tileKey, fadeStart);
    }

    const opacity = Math.min(1, (now - fadeStart) / this.IDEAL_TILE_FADE_MS);
    if (opacity >= 1) {
      this.idealTileFadeStartMs.delete(tileKey);
      this.settledIdealTileKeys.add(tileKey);
      return 1;
    }

    return opacity;
  }

  private hasLoadedBackingCoverage(tile: WmtsTile, backingTiles: WmtsTile[]): boolean {
    for (const backingTile of backingTiles) {
      if (backingTile.z > tile.z) {
        continue;
      }

      const scale = 2 ** (tile.z - backingTile.z);
      if (Math.floor(tile.x / scale) === backingTile.x && Math.floor(tile.y / scale) === backingTile.y) {
        return true;
      }
    }

    return false;
  }

  private getCurrentTileReplacementOpacity(
    currentTileX: number,
    currentTileY: number,
    currentTileZoom: number,
    replacementIdealTiles?: readonly TileOpacity[],
  ): number {
    if (!replacementIdealTiles || replacementIdealTiles.length === 0) {
      return 0;
    }

    let replacementOpacity = 0;

    for (const { tile, opacity } of replacementIdealTiles) {
      if (tile.z > currentTileZoom) {
        const scale = 2 ** (tile.z - currentTileZoom);
        if (Math.floor(tile.x / scale) === currentTileX && Math.floor(tile.y / scale) === currentTileY) {
          replacementOpacity = Math.max(replacementOpacity, opacity);
        }
        continue;
      }

      const scale = 2 ** (currentTileZoom - tile.z);
      if (Math.floor(currentTileX / scale) === tile.x && Math.floor(currentTileY / scale) === tile.y) {
        replacementOpacity = Math.max(replacementOpacity, opacity);
      }
    }

    return replacementOpacity;
  }

  private getVisibleScaledTileRange(
    tile: WmtsTile,
    currentTileZoom: number,
    viewport: MapViewport,
  ): { minDx: number; maxDx: number; minDy: number; maxDy: number } | null {
    const scaleFactor = 2 ** (currentTileZoom - tile.z);
    const tileMinX = tile.x * scaleFactor;
    const tileMaxX = tileMinX + scaleFactor - 1;
    const tileMinY = tile.y * scaleFactor;
    const tileMaxY = tileMinY + scaleFactor - 1;

    const visibleMinX = Math.max(tileMinX, Math.floor(viewport.viewportArea.xyMin[0]));
    const visibleMaxX = Math.min(tileMaxX, Math.floor(viewport.viewportArea.xyMax[0]));
    const visibleMinY = Math.max(tileMinY, Math.floor(viewport.viewportArea.xyMin[1]));
    const visibleMaxY = Math.min(tileMaxY, Math.floor(viewport.viewportArea.xyMax[1]));

    if (visibleMinX > visibleMaxX || visibleMinY > visibleMaxY) {
      return null;
    }

    return {
      minDx: visibleMinX - tileMinX,
      maxDx: visibleMaxX - tileMinX,
      minDy: visibleMinY - tileMinY,
      maxDy: visibleMaxY - tileMinY,
    };
  }

  private getInstantiatedViewportArea(viewport: MapViewport): MapViewport["viewportArea"] {
    return {
      ...viewport.viewportArea,
      xyMin: [Math.floor(viewport.viewportArea.xyMin[0]), Math.floor(viewport.viewportArea.xyMin[1])],
      xyMax: [Math.floor(viewport.viewportArea.xyMax[0]), Math.floor(viewport.viewportArea.xyMax[1])],
      zLodBias: 0,
    };
  }

  private renderMainpassTextures(
    gl: WebGL2RenderingContext,
    mainpass: MainpassProgram,
    previousTexture: WebGLTexture,
    currentTexture: WebGLTexture,
    opacity: number,
    temporalInterpolation: number,
    size: GridDimension,
  ): void {
    gl.uniform4f(mainpass.u_props, opacity, temporalInterpolation, size.width, size.height);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, currentTexture);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, previousTexture);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private prepareTemporalFade(
    gl: WebGL2RenderingContext,
    size: GridDimension,
    nextTimeKey: string | null,
    nextViewKey: string,
    disableTransitions: boolean,
  ): void {
    if (disableTransitions) {
      this.finishTimeStepFade(gl);
      return;
    }

    const isSameView = this.lastRenderedViewKey === nextViewKey;
    const isTimeChange =
      this.lastRenderedTimeKey != null && nextTimeKey != null && this.lastRenderedTimeKey !== nextTimeKey;

    if (isTimeChange && isSameView && this._prepassFramebuffer && !this.pendingTimeStepFade) {
      this.disposeFramebuffer(gl, this._previousPrepassFramebuffer);
      this._previousPrepassFramebuffer = this._prepassFramebuffer;
      this._prepassFramebuffer = undefined;
      this.pendingTimeStepFade = true;
      this.timeStepFadeStartMs = null;
      this.previousRenderedTiles = [];
      this.idealTileFadeStartMs.clear();
      this.settledIdealTileKeys.clear();
      return;
    }

    if (this.pendingTimeStepFade && !isSameView) {
      this.finishTimeStepFade(gl);
    }

    const previousFb = this._previousPrepassFramebuffer;
    if (
      previousFb &&
      (previousFb.dataRasterizationSize.width !== size.width || previousFb.dataRasterizationSize.height !== size.height)
    ) {
      this.finishTimeStepFade(gl);
    }
  }

  private syncAnimationMode(gl: WebGL2RenderingContext, isAnimationMode: boolean): void {
    if (isAnimationMode && !this.lastAnimationMode) {
      this.previousRenderedTiles = [];
      this.idealTileFadeStartMs.clear();
      this.settledIdealTileKeys.clear();
      this.finishTimeStepFade(gl);
    }

    this.lastAnimationMode = isAnimationMode;
  }

  private finishTimeStepFade(gl: WebGL2RenderingContext): void {
    this.pendingTimeStepFade = false;
    this.timeStepFadeStartMs = null;
    this.disposeFramebuffer(gl, this._previousPrepassFramebuffer);
    this._previousPrepassFramebuffer = undefined;
  }

  private getTimeStepFadeOpacity(): number {
    if (!this.pendingTimeStepFade || this.timeStepFadeStartMs == null) {
      return 0;
    }

    return Math.min(1, (Date.now() - this.timeStepFadeStartMs) / this.TIME_STEP_FADE_MS);
  }

  private getTileTimeKey(tileSets: WmtsRenderableTileSets): string | null {
    const sampleTile = tileSets.idealTiles[0] ?? tileSets.backingTiles[0];
    return sampleTile?.datetime ?? null;
  }

  private getViewKey(viewport: MapViewport, size: GridDimension): string {
    const area = viewport.viewportArea;
    return `${viewport.zoom}|${area.xyMin[0]}|${area.xyMin[1]}|${area.xyMax[0]}|${area.xyMax[1]}|${area.z}|${size.width}|${size.height}`;
  }

  private disposeFramebuffer(gl: WebGL2RenderingContext, framebuffer?: PrepassFramebuffer): void {
    if (!framebuffer) {
      return;
    }

    gl.deleteTexture(framebuffer.color);
    if (typeof gl.deleteFramebuffer === "function") {
      gl.deleteFramebuffer(framebuffer.framebuffer);
    }
  }

  private getPrepassFramebuffer(
    gl: WebGL2RenderingContext,
    dataRasterizationSize: GridDimension,
    interpolationMode: InterpolationMode,
  ): PrepassFramebuffer {
    if (this._prepassFramebuffer == null) {
      this._prepassFramebuffer = createPrepassFramebuffer(gl, this.scene.getRenderSize(), this.interpolationMode);
    } else {
      const size = this._prepassFramebuffer.dataRasterizationSize;
      if (size.width !== dataRasterizationSize.width || size.height !== dataRasterizationSize.height) {
        const oldFramebuffer = this._prepassFramebuffer;
        const v = createPrepassFramebuffer(gl, dataRasterizationSize, interpolationMode);
        this.disposeFramebuffer(gl, oldFramebuffer);
        this._prepassFramebuffer = v;
      }
    }
    return this._prepassFramebuffer;
  }
}
